/**
 * @fileoverview Custom hook for managing session authentication state and
 * session management for the application.
 */

import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { clearExpiredSession } from '@/api';
import { onAuthStateChanged, type User } from '../session/auth';
import type { AuthSession } from '../context/AuthSessionContext';

export interface UseAuthReturn {
  session: AuthSession | null;
  setSession: Dispatch<SetStateAction<AuthSession | null>>;
  loading: boolean;
}

export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // An expired token can't authenticate anything, so start signed out and let
    // the app shell redirect to sign-in rather than render a page full of 401s.
    clearExpiredSession();

    // Returns an `unsubscribe` function to be called during teardown
    const unsubscribe = onAuthStateChanged((user: User | null) => {
      if (user) {
        setSession({
          user: {
            name: user.displayName || '',
            email: user.email || '',
            image: user.photoURL || '',
          },
        });
      } else {
        setSession(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    session,
    setSession,
    loading,
  };
}